import { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { ClerkProvider, useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { isAdminRole, ROLES } from '@/lib/roles';
import { fetchMyAccess } from '@/lib/teamApi';
import { isAllowedEmail } from '@/lib/emailPolicy';

const AuthContext = createContext(null);

const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

function normalizeRole(role) {
  if (role === ROLES.ADMIN || role === ROLES.MEMBER) return role;
  return ROLES.MEMBER;
}

function AuthBridge({ children }) {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const { signOut, getToken } = useClerkAuth();
  const [resolvedRole, setResolvedRole] = useState(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !clerkUser) return;
    const email = clerkUser.primaryEmailAddress?.emailAddress || '';
    if (email && !isAllowedEmail(email)) {
      // Immediately sign out any account outside the allowed domain.
      signOut();
    }
  }, [isLoaded, isSignedIn, clerkUser, signOut]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setResolvedRole(null);
      return undefined;
    }

    let cancelled = false;

    const syncAccess = async () => {
      try {
        const access = await fetchMyAccess(getToken);
        if (cancelled) return;

        const nextRole = normalizeRole(access.role);
        setResolvedRole(nextRole);

        const metadataRole = normalizeRole(clerkUser?.publicMetadata?.role);
        if (nextRole !== metadataRole && clerkUser) {
          await clerkUser.reload();
        }
      } catch {
        if (!cancelled) {
          setResolvedRole(normalizeRole(clerkUser?.publicMetadata?.role));
        }
      }
    };

    syncAccess();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken, clerkUser]);

  const role = isSignedIn
    ? resolvedRole || normalizeRole(clerkUser?.publicMetadata?.role)
    : null;

  const user = isSignedIn
    ? {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        name:
          clerkUser.fullName ||
          [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(' ') ||
          clerkUser.username ||
          'User',
        imageUrl: clerkUser.imageUrl,
        role,
      }
    : null;

  const logout = useCallback(async () => {
    setResolvedRole(null);
    await signOut();
  }, [signOut]);

  const value = {
    user,
    role,
    isAdmin: isSignedIn ? isAdminRole(role) : false,
    isAuthenticated: !!isSignedIn,
    loading: !isLoaded || (isSignedIn && resolvedRole === null),
    logout,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const AuthProvider = ({ children }) => {
  if (!publishableKey) {
    return (
      <AuthContext.Provider
        value={{
          user: null,
          role: null,
          isAdmin: false,
          isAuthenticated: false,
          loading: false,
          logout: async () => {},
          getToken: async () => null,
          clerkMissing: true,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl="/"
      signInUrl="/login"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/dashboard"
    >
      <AuthBridge>{children}</AuthBridge>
    </ClerkProvider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
